import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import CountryFlag from "@/app/domain/callflow/timetables/holidaysets/calendar/CountryFlag";
import { NEW_PBX } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumeric from "@/design/forms/InputNumeric";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import StringForm from "@/design/forms/StringForm";
import CountryCodes from "@/resources/countrycode";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viArrowRight } from "@sippy-platform/valkyrie";

import { Button, Container, Grid, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import pbxReducer from "./pbxReducer";

function Pbx() {
  const { context } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const auth = useSelector((state) => state.auth);
  const pbxStore = useSelector((state) => state.backstage.pbxes);

  const {
    value: pbx,
    dispatch: pbxDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PBX,
    reducer: pbxReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      maxSippeers: number().required({ message: "Maximum users is required." }),
      maxConcurrentLines: number().required({
        message: "Maximum lines is required.",
      }),
      zip: string().required({ message: "ZIP is required." }),
    }),
    onSubmit: () => Api.putPbx(pbx, true),
    onSuccess: (response) => {
      if (!pbx.id) {
        pbxDispatch({ type: "id", value: response.data.pbx.id });
        navigate(`../${response.data.pbx.context}`);
      }

      Promise.resolve(Api.getPbxes());
    },
  });

  // Get general data
  useEffect(() => {
    if (context === pbx.context || !context) {
      return;
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getDomain({ context }))
      .then((response) => pbxDispatch({ type: "set", value: response.data.pbx }))
      .finally(() => toggleLoadingState(false));
  }, [context, pbx.context, pbxDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    pbxDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getPbxes()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${pbx.name || "PBX"} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        {pbxStore.meta.count ? (
          <>
            <HeaderCard
              avatar={
                !matches ? (
                  <IconButton variant="avatar" component={NavLink} to={"/backstage/pbxes"}>
                    <Valkyrie icon={viArrowLeft} />
                    <Avatar text={pbx.name} />
                  </IconButton>
                ) : (
                  <Avatar text={pbx.name} />
                )
              }
              action={
                <>
                  <SaveIndicator formState={formState} errors={allErrors} save={save} />
                  <Button
                    color="inherit"
                    startIcon={<Valkyrie icon={viArrowRight} />}
                    component={NavLink}
                    to={`/pbx/${pbx.context}/dashboard`}
                  >
                    Open
                  </Button>
                  {auth.accessLevel <= 1 && (
                    <DeleteFlow
                      key={pbx.id || "nk"}
                      isActionable={!!pbx?.id}
                      onDelete={() => Api.deletePbx(pbx)}
                      name={pbx.name}
                      deleteInfo="Are you sure you want to delete this PBX? This action cannot be undone."
                      onDeleteFinish={onDeleteFinish}
                    />
                  )}
                </>
              }
              title={pbx.name || "Unnamed PBX"}
            />
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText required id="name" label="Name" onChange={formHandler} value={pbx.name} error={errors} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="zip"
                  label="Postal code"
                  onChange={formHandler}
                  value={pbx.zip}
                  error={errors}
                />
              </Grid>
              {auth.accessLevel <= 1 && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputText
                    id="customerReference"
                    label="Customer reference"
                    onChange={formHandler}
                    value={pbx.customerReference}
                    error={errors}
                  />
                </Grid>
              )}
            </FormGroup>
            <FormGroup title="Calls">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  required
                  id="defaultCountryCallingCode"
                  label="Default country calling code"
                  onChange={formHandler}
                  value={`+${pbx.defaultCountryCallingCode}`}
                  selectValue="phone"
                  selects={CountryCodes}
                  getOptionLabel={(option) => `${option.phone} \u00b7 ${option.name}`}
                  renderOptionLabel={(option) => (
                    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                      <CountryFlag code={option.code} />
                      <span>{`${option.phone} \u00b7 ${option.name}`}</span>
                    </Stack>
                  )}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="defaultPhoneNumber"
                  label="Default phone number"
                  onChange={formHandler}
                  value={pbx.defaultPhoneNumber}
                  error={errors}
                  selectValue="did"
                  selects={pbx.phoneNumbers ?? []}
                  getOptionLabel={(option) => option.did}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Usage limits">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="maxSippeers"
                  label="Maximum users"
                  onChange={formHandler}
                  min={1}
                  value={pbx.maxSippeers}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="maxConcurrentLines"
                  label="Maximum lines"
                  onChange={formHandler}
                  min={1}
                  value={pbx.maxConcurrentLines}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="AI">
              <Grid size={{ xs: 12 }}>
                <InputToggle
                  id="enableAi"
                  error={errors}
                  onChange={formHandler}
                  states={[false, true]}
                  value={pbx.enableAi}
                  primary="Analyze calls to extract insights and transcripts"
                  secondary="When a call is finished, Sippy will analyze the recording with AI to create a transcript, extract insights such as sentiment, a summary of the call and the language of the call. This information will show up in the Call Log. In order for a call to be analyzed, it must be recorded. Recordings are always stored on servers within the EU for 60 days. The analysis of the recording also happens within the EU and does not store the recording. AI analysis cannot be retroactively applied."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="About">
              {auth.accessLevel <= 1 && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="ID" text={pbx.id} />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Context" text={pbx.context} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Account code" text={pbx.accountcode} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Partner" text={pbx.partner?.name} />
              </Grid>
            </FormGroup>
          </>
        ) : (
          <>
            <Typography variant="h5" style={{ textAlign: "center" }}>
              Create a PBX to get started.
            </Typography>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default Pbx;
